import type { GameContext } from "../GameContext";

export interface CharacterSummary {
  id: string;
  name: string;
  jobId: string;
  jobTier: number;
  baseLevel: number;
  currentZone: string;
}

export type ListCharactersResult =
  | { ok: true; characters: CharacterSummary[] }
  | { ok: false; code: string; message: string };

export type SelectCharacterResult =
  | { ok: true; character: CharacterSummary; websocketUrl: string }
  | { ok: false; code: string; message: string };

/**
 * Per M03 (player-worker): GET/POST /api/v1/characters. Per 2.2's
 * documented world-do flow: GET /api/v1/zones/:code/connect resolves a
 * websocket_url without itself upgrading — this controller fetches that
 * URL and hands it to NetworkManager.connect(), it does not construct any
 * WebSocket URL itself (2.1's "only place fetch()/WebSocket" rule lives in
 * NetworkManager, not here).
 */
export class CharacterSelectController {
  constructor(private readonly ctx: GameContext) {}

  async listCharacters(): Promise<ListCharactersResult> {
    const result = await this.ctx.network.rest.get<CharacterSummary[]>("/api/v1/characters");
    if (!result.ok) {
      return { ok: false, code: result.code, message: result.message };
    }
    return { ok: true, characters: result.data };
  }

  /**
   * Per StateManager: character_select -> connecting is the only legal
   * next step; connecting then resolves to in_world (success) or back to
   * character_select (failure to establish the zone connection).
   */
  async selectCharacter(characterId: string): Promise<SelectCharacterResult> {
    const charResult = await this.ctx.network.rest.get<CharacterSummary>(`/api/v1/characters/${characterId}`);
    if (!charResult.ok) {
      return { ok: false, code: charResult.code, message: charResult.message };
    }

    this.ctx.state.transition("connecting");

    // BUG FIX: this previously called the connect endpoint with no query
    // string at all, but world-worker's route (routes/zones.ts) requires
    // a `characterId` query parameter and returns a 400 VALIDATION_ERROR
    // ("characterId is required") without it — every "Enter World"
    // attempt failed this way, discovered via a real character-select
    // attempt against the deployed client. characterId is already
    // available as this method's own parameter; it just wasn't being
    // forwarded.
    const connectResult = await this.ctx.network.rest.get<{ websocketUrl: string }>(
      `/api/v1/zones/${charResult.data.currentZone}/connect?characterId=${encodeURIComponent(characterId)}&characterName=${encodeURIComponent(charResult.data.name)}&jobId=${encodeURIComponent(charResult.data.jobId)}`
    );
    if (!connectResult.ok) {
      this.ctx.state.transition("character_select");
      return { ok: false, code: connectResult.code, message: connectResult.message };
    }

    this.ctx.selectedCharacterId = characterId;
    return { ok: true, character: charResult.data, websocketUrl: connectResult.data.websocketUrl };
  }
}
